import { Bell, Check, CheckCheck, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery, useAction } from "wasp/client/operations";
import {
  getMyNotifications,
  markNotificationsRead,
  dismissNotifications,
} from "wasp/client/operations";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../client/components/ui/dropdown-menu";
import { Button } from "../client/components/ui/button";
import useTranslation from "../client/hooks/useTranslation";
import { cn } from "../client/utils";
import { NOTIFICATION_TYPES, type NotificationDto } from "./types";
import { CURRENCY } from "../shared/currency";

type Translations = ReturnType<typeof useTranslation>;

/** Preserve un apercu court : la cloche n'est pas un historique. */
const MAX_VISIBLE = 10;

/** Traduit la periode d'un rapport. Renvoie null si la valeur est inconnue,
 *  pour ne jamais afficher une cle brute a l'utilisateur. */
function periodLabel(period: unknown, t: Translations): string | null {
  switch (period) {
    case "monthly":
      return t.notificationsCenter.periodMonthly;
    case "quarterly":
      return t.notificationsCenter.periodQuarterly;
    case "annual":
      return t.notificationsCenter.periodAnnual;
    default:
      return null;
  }
}

/** Fonction pure : notification -> libelle affichable.
 *  Renvoie null pour tout type inconnu, la ligne n'est alors pas rendue.
 *  On n'affiche jamais un `type` brut. */
export function labelFor(
  type: string,
  metadata: Record<string, unknown> | null,
  t: Translations,
): string | null {
  const meta = metadata ?? {};

  switch (type) {
    case NOTIFICATION_TYPES.REPORT_READY: {
      const period = periodLabel(meta.period, t);
      if (!period) return null;
      return t.notificationsCenter.reportReady.replace("{period}", period);
    }
    case NOTIFICATION_TYPES.PLAN_UPGRADED: {
      const plan = typeof meta.toPlan === "string" ? meta.toPlan : null;
      if (!plan) return null;
      return t.notificationsCenter.planUpgraded.replace("{plan}", plan);
    }
    case NOTIFICATION_TYPES.PLAN_DOWNGRADED: {
      // Retrogradation subie (impaye) : le libelle en donne la raison, sinon
      // l'utilisateur lit « passe a Free » sans comprendre pourquoi. Le plan
      // porte ici est celui qu'il PERD, d'ou une cle distincte.
      if (meta.reason === "payment_failed") {
        const previous =
          typeof meta.previousPlanName === "string" ? meta.previousPlanName : null;
        if (!previous) return null;
        return t.notificationsCenter.planDowngradedPaymentFailed.replace(
          "{plan}",
          previous,
        );
      }
      const plan = typeof meta.toPlan === "string" ? meta.toPlan : null;
      if (!plan) return null;
      return t.notificationsCenter.planDowngraded.replace("{plan}", plan);
    }
    case NOTIFICATION_TYPES.PAYMENT_FAILED: {
      // Rappels du job : on affiche le compte a rebours. L'ouverture de grace
      // (webhook) n'a pas ce champ et garde le libelle simple.
      const days =
        typeof meta.daysRemaining === "number" ? meta.daysRemaining : null;
      if (days === null) return t.notificationsCenter.paymentFailed;
      return t.notificationsCenter.paymentFailedWithDays.replace(
        "{days}",
        String(days),
      );
    }
    case NOTIFICATION_TYPES.ACCOUNT_CHALLENGE_COMPLETED: {
      const name = typeof meta.accountName === "string" ? meta.accountName : null;
      if (!name) return null;
      return t.notificationsCenter.accountChallengeCompleted.replace("{account}", name);
    }
    case NOTIFICATION_TYPES.ACCOUNT_CLOSED_BROKER: {
      const name = typeof meta.accountName === "string" ? meta.accountName : null;
      if (!name) return null;
      return t.notificationsCenter.accountClosedBroker.replace("{account}", name);
    }
    case NOTIFICATION_TYPES.ACCOUNT_DRAWDOWN_BREACH: {
      const name = typeof meta.accountName === "string" ? meta.accountName : null;
      if (!name) return null;
      return t.notificationsCenter.accountDrawdownBreach.replace("{account}", name);
    }
    case NOTIFICATION_TYPES.SUBSCRIPTION_CANCEL_QUESTION: {
      const name = typeof meta.accountName === "string" ? meta.accountName : null;
      if (!name) return null;
      return t.notificationsCenter.subscriptionCancelQuestion.replace("{account}", name);
    }
    case NOTIFICATION_TYPES.SUBSCRIPTION_RESET_REACTIVATE: {
      const name = typeof meta.accountName === "string" ? meta.accountName : null;
      if (!name) return null;
      return t.notificationsCenter.subscriptionResetReactivate.replace("{account}", name);
    }
    case NOTIFICATION_TYPES.CHARGE_UPCOMING: {
      const name = typeof meta.name === "string" ? meta.name : null;
      const amount = typeof meta.amount === "number" ? meta.amount : null;
      const due = typeof meta.dueDate === "string" ? meta.dueDate : null;
      if (!name || amount === null || !due) return null;
      const formatted = new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: CURRENCY,
        maximumFractionDigits: 2,
      }).format(amount);
      // `dueDate` = clé yyyy-MM-dd : midi UTC, pour ne pas glisser d'un jour à l'affichage.
      const dateLabel = new Date(`${due}T12:00:00Z`).toLocaleDateString();
      return t.notificationsCenter.chargeUpcoming
        .replace("{amount}", formatted)
        .replace("{name}", name)
        .replace("{date}", dateLabel);
    }
    case NOTIFICATION_TYPES.PAYOUT_DETECTED: {
      const name = typeof meta.accountName === "string" ? meta.accountName : null;
      const amount = typeof meta.amount === "number" ? meta.amount : null;
      if (!name || amount === null) return null;
      const formatted = new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: CURRENCY,
        maximumFractionDigits: 2,
      }).format(amount);
      return t.notificationsCenter.payoutDetected
        .replace("{amount}", formatted)
        .replace("{account}", name);
    }
    case NOTIFICATION_TYPES.PROP_FIRM_ADJUSTMENT_DETECTED: {
      const name = typeof meta.accountName === "string" ? meta.accountName : null;
      const amount = typeof meta.amount === "number" ? meta.amount : null;
      const session = typeof meta.session === "string" ? meta.session : null;
      if (!name || amount === null || !session) return null;
      const formatted = new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: CURRENCY,
        maximumFractionDigits: 2,
      }).format(amount);
      // `session` = clé yyyy-MM-dd de la séance : midi UTC, pour ne pas glisser d'un jour à l'affichage.
      const sessionLabel = new Date(`${session}T12:00:00Z`).toLocaleDateString();
      return t.notificationsCenter.propFirmAdjustmentDetected
        .replace("{amount}", formatted)
        .replace("{account}", name)
        .replace("{session}", sessionLabel);
    }
    case NOTIFICATION_TYPES.WALLET_TRANSFER_DETECTED: {
      const name = typeof meta.accountName === "string" ? meta.accountName : null;
      const amount = typeof meta.amount === "number" ? meta.amount : null;
      const gross = typeof meta.grossAmount === "number" ? meta.grossAmount : null;
      if (!name || amount === null || gross === null) return null;
      const fmt = new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: CURRENCY,
        maximumFractionDigits: 2,
      });
      const template = meta.toConfirm === true
        ? t.notificationsCenter.walletTransferDetectedToConfirm
        : t.notificationsCenter.walletTransferDetected;
      return template
        .replace("{gross}", fmt.format(gross))
        .replace("{amount}", fmt.format(amount))
        .replace("{account}", name);
    }
    case NOTIFICATION_TYPES.TRADOVATE_ACCOUNTS_DISCOVERED: {
      // Singulier et pluriel portes par deux cles distinctes : « 1 nouveaux
      // comptes » se lit comme un bug d'affichage.
      const count = typeof meta.count === "number" ? meta.count : null;
      if (count === null || count < 1) return null;
      const template =
        count === 1
          ? t.notificationsCenter.tradovateAccountsDiscoveredOne
          : t.notificationsCenter.tradovateAccountsDiscovered;
      return template.replace("{count}", String(count));
    }
    case NOTIFICATION_TYPES.TRADOVATE_REALTIME_INTERRUPTED: {
      // La prop firm est ce que l'utilisateur reconnait ; a defaut, le nom
      // du broker plutot qu'un trou dans la phrase.
      const propFirm =
        typeof meta.propFirmName === "string" && meta.propFirmName.trim()
          ? meta.propFirmName
          : "Tradovate";
      return t.notificationsCenter.tradovateRealtimeInterrupted.replace("{propFirm}", propFirm);
    }
    case NOTIFICATION_TYPES.TRADOVATE_NEEDS_REAUTH: {
      const propFirm =
        typeof meta.propFirmName === "string" && meta.propFirmName.trim()
          ? meta.propFirmName
          : "Tradovate";
      return t.notificationsCenter.tradovateNeedsReauth.replace("{propFirm}", propFirm);
    }
    default:
      // Les notifications mentor heritees partagent un libelle generique tant
      // que leurs ecritures ne sont pas repliees sur la nouvelle table.
      if (type.startsWith("mentor_")) return t.notificationsCenter.mentorFallback;
      return null;
  }
}

/**
 * `rail` : bouton du rail sombre de l'Advanced (rendu historique).
 * `row` : ligne de la sidebar Lite (icône, libellé, compteur), aux couleurs du thème.
 * `icon` : bouton d'en-tête du Lite mobile, aux couleurs du thème.
 */
export default function NotificationsBell({ trigger = "rail", side = "right", align = "end" }: {
  trigger?: "rail" | "row" | "icon";
  side?: "right" | "bottom" | "top";
  align?: "start" | "end";
} = {}) {
  const t = useTranslation();
  const navigate = useNavigate();

  // 60s et non 30s : aucun de ces evenements n'est temps reel (rapport
  // periodique, changement de plan, echec de paiement), et cela divise par
  // deux la charge de polling.
  const { data, refetch } = useQuery(getMyNotifications, undefined, {
    refetchInterval: 60_000,
  });

  const markRead = useAction(markNotificationsRead);
  const dismiss = useAction(dismissNotifications);

  const items = (data?.items ?? []) as unknown as NotificationDto[];
  const unreadCount = data?.unreadCount ?? 0;

  // Les types inconnus sont filtres en amont : une ligne sans libelle
  // traduisible ne doit pas creer un trou dans la liste.
  const rows = items
    .map((n) => ({ notification: n, label: labelFor(n.type, n.metadata, t) }))
    .filter((row): row is { notification: NotificationDto; label: string } => row.label !== null)
    .slice(0, MAX_VISIBLE);

  async function handleClick(n: NotificationDto) {
    if (!n.isRead) {
      await markRead({ ids: [n.id] });
    }
    if (n.link) {
      navigate(n.link);
    }
    refetch();
  }

  async function handleMarkAllRead() {
    // ids omis = tout marquer comme lu cote serveur, y compris ce qui n'est
    // pas charge dans cet apercu.
    await markRead({});
    refetch();
  }

  /** Retire la notification du panneau. Elle reste en base, seule la cloche
   *  cesse de la lister. */
  async function handleDismiss(n: NotificationDto) {
    await dismiss({ ids: [n.id] });
    refetch();
  }

  async function handleDismissAll() {
    // ids omis = tout archiver cote serveur, y compris au dela de MAX_VISIBLE :
    // vider un apercu de 10 lignes en laissant les suivantes remonter derriere
    // donnerait un panneau qui ne se vide jamais.
    await dismiss({});
    refetch();
  }

  const ariaLabel =
    unreadCount > 0
      ? t.notificationsCenter.unreadAriaLabel.replace("{count}", String(unreadCount))
      : t.notificationsCenter.bellAriaLabel;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {trigger === "row" ? (
          <button
            type="button"
            aria-label={ariaLabel}
            className="flex w-full items-center gap-3 rounded-xl px-3 h-10 text-sm text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
          >
            <Bell className="h-[18px] w-[18px]" />
            <span className="flex-1 text-left">{t.notificationsCenter.title}</span>
            {unreadCount > 0 && (
              <span className="min-w-5 h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-[11px] font-semibold grid place-items-center">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>
        ) : trigger === "icon" ? (
          <button
            type="button"
            aria-label={ariaLabel}
            className="relative grid h-9 w-9 place-items-center rounded-lg text-muted-foreground hover:bg-muted"
          >
            <Bell size={18} />
            {unreadCount > 0 && (
              <span aria-hidden className="absolute right-1 top-1 h-2 w-2 rounded-full bg-destructive ring-2 ring-background" />
            )}
          </button>
        ) : (
        <button
          type="button"
          aria-label={ariaLabel}
          className="relative flex h-10 w-10 items-center justify-center rounded-lg text-white/80 transition-colors hover:bg-white/10 hover:text-white"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            // Pastille et non compteur : le rail fait 60px, un chiffre y est
            // illisible. L'anneau couleur rail decoupe l'icone derriere.
            <span
              aria-hidden
              className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-destructive ring-2 ring-navRail"
            />
          )}
        </button>
        )}
      </DropdownMenuTrigger>

      {/* Radix porte le panneau hors du rail : sans cela, la colonne sticky de
          60px et son contexte d'empilement le rogneraient. */}
      <DropdownMenuContent side={side} align={align} className="w-80">
        <div className="flex items-center justify-between gap-1 px-3 py-2">
          <span className="text-sm font-semibold">{t.notificationsCenter.title}</span>
          <div className="flex items-center gap-1">
            {/* Icones et non libelles : les deux textes complets debordaient du
                panneau de 320px. Le sens reste porte par aria-label + title. */}
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="icon"
                aria-label={t.notificationsCenter.markAllRead}
                title={t.notificationsCenter.markAllRead}
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                onClick={(event) => {
                  event.preventDefault();
                  handleMarkAllRead();
                }}
              >
                <CheckCheck className="h-4 w-4" />
              </Button>
            )}
            {/* Conditionne aux lignes affichees et non aux non lues : une fois
                tout lu, c'est precisement le moment ou l'on veut vider. */}
            {rows.length > 0 && (
              <Button
                variant="ghost"
                size="icon"
                aria-label={t.notificationsCenter.dismissAll}
                title={t.notificationsCenter.dismissAll}
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                onClick={(event) => {
                  event.preventDefault();
                  handleDismissAll();
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        <DropdownMenuSeparator />

        {rows.length === 0 ? (
          <p className="px-3 py-4 text-center text-sm text-muted-foreground">
            {t.notificationsCenter.empty}
          </p>
        ) : (
          rows.map(({ notification, label }) => (
            <DropdownMenuItem
              key={notification.id}
              className={cn(
                "group flex cursor-pointer items-start gap-2 px-3 py-2",
                !notification.isRead && "bg-accent/50",
              )}
              onClick={() => handleClick(notification)}
            >
              <span
                aria-hidden
                className={cn(
                  "mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full",
                  notification.isRead ? "bg-transparent" : "bg-primary",
                )}
              />
              <span
                className={cn(
                  "flex-1 text-sm leading-snug",
                  !notification.isRead && "font-medium",
                )}
              >
                {label}
              </span>
              {/* onSelect n'existe pas sur un bouton imbrique : c'est le clic
                  qui doit etre stoppe, sinon il remonte au DropdownMenuItem qui
                  navigue puis ferme le panneau. preventDefault empeche en plus
                  Radix de traiter le clic comme une selection d'item. */}
              <button
                type="button"
                aria-label={t.notificationsCenter.dismiss}
                title={t.notificationsCenter.dismiss}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleDismiss(notification);
                }}
                className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:bg-accent hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
              >
                <Check className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
